import React from 'react'
import { PlanType } from './type'
import { PlanGroup, PlanGroupType } from './PlanGroup'
import { JpeEmptyPlan } from '../jpe-components/JpeEmptyPlan'
import { useLpAvailabilityPageStore } from '../../../store/lpAvailabilityPageStore'

export const PlanList = (props: { planList: PlanType[] }) => {
  const { planList } = props
  const { isJpe } = useLpAvailabilityPageStore()
  const roomTypeOrders = isJpe
    ? [
        'JPE House',
        'Whole building rental',
        'Aparthotel',
        'Hotel',
        'Ryokan (Japanese inn)',
        'Guesthouse',
        'Dormitory',
        'Private home',
      ]
    : ['保育園留学の寮', '一棟貸し', 'アパートメントホテル', 'ホテル', '旅館', 'ゲストハウス', '民宿']

  const groupPlansByRoomId = (roomTypePlans: PlanType[]): PlanGroupType[] => {
    const byRoomId = new Map<number, PlanGroupType>()

    roomTypePlans.forEach((plan) => {
      // [FB-76] 部屋なしプランは room が無いため、roomId=0 でまとめて1グループにする
      const roomId = plan.room?.id ?? 0
      const existing = byRoomId.get(roomId)
      if (existing) {
        existing.plans.push(plan)
      } else {
        byRoomId.set(roomId, { roomId, room: plan.room, plans: [plan] })
      }
    })

    return Array.from(byRoomId.values()).map((g) => ({
      ...g,
      plans: g.plans.sort((a, b) => (a.roomDisplayOrder || 0) - (b.roomDisplayOrder || 0) || a.planId - b.planId),
    }))
  }

  // [FB-76] room を持たない（roomType が undefined の）部屋なしプランも unmatchedPlans に入り、宿泊なしグループへ流れる
  const unmatchedPlans = planList.filter((plan) => !roomTypeOrders.includes(plan.room?.roomType ?? ''))
  const noAccommodationLabel = isJpe ? 'School Fee Only (No Accommodation)' : '宿泊なし'

  const plansGroupedByRoomType = [
    ...roomTypeOrders.map((roomType) => ({
      roomType,
      planGroups: groupPlansByRoomId(
        planList.filter((plan) => plan.room?.roomType === roomType).sort((a, b) => (a.room?.order || 0) - (b.room?.order || 0)),
      ),
    })),
    ...(unmatchedPlans.length > 0 ? [{ roomType: noAccommodationLabel, planGroups: groupPlansByRoomId(unmatchedPlans) }] : []),
  ].filter((group) => group.planGroups.length > 0)

  if (isJpe && planList.length === 0)
    return (
      <>
        <div className="text-sm tracking-wider px-4 pb-4">No available dates match your search criteria.</div>
        <JpeEmptyPlan />
      </>
    )

  if (planList.length === 0) {
    return (
      <>
        <p className="px-4 pb-4 font-semibold leading-6">現在の条件では該当する空き日程がありません。</p>
        <div id="consultation">
          <div className="consultation-wrapper">
            <div className="image">
              <img src="/images/reservation_lp/consultation.webp" alt="consultation" className="rounded-12" />
            </div>
            <div className="content">
              <div className="block w-full">
                <h3 className="font-size-18 tracking-wider font-a1 text-green text-left md:text-center mb-2 md:font-size-28">
                  留学先選びに迷ったら
                </h3>
                <div className="font-size-14 tracking-wider gallery-item-text text-left md:text-center">
                  保育園留学に行きたいけれど、どんなふうに留学先を選んだら良いかわからない
                </div>
                <div className="font-size-14 tracking-wider gallery-item-text text-left md:text-center mb-6">
                  そんな方のために、さまざまなサービスをご用意しております。
                </div>
              </div>
              <div className="flex flex-wrap justify-center text-center gap-2 consultation-margin">
                <div className="circle-text">
                  <div className="font-size-12 md:font-size-20">
                    留学先の
                    <br />
                    ご提案
                  </div>
                </div>
                <div className="circle-text">
                  <div className="font-size-12 md:font-size-20">
                    トータル予算の
                    <br />
                    ご案内
                  </div>
                </div>
                <div className="circle-text">
                  <div className="font-size-12 md:font-size-20">
                    留学プランの
                    <br />
                    ご相談
                  </div>
                </div>
              </div>
              <div className="block w-full">
                <h3 className="font-size-18 tracking-wider font-a1 text-green text-left md:text-center mb-2 md:font-size-28">
                  LINEで相談
                </h3>
                <div className="text-left mb-4 md:text-center md:mb-8">
                  <p className="font-size-14 tracking-wider gallery-item-text">
                    チャット（会話）でいただいたご質問に、プランナーがお答えします。
                  </p>
                  <p className="font-size-14 tracking-wider gallery-item-text">【受付】平日 9:00 - 18:00（土日祝休）</p>
                </div>
                <button className="bg-green text-white w-full text-center px-6 py-3 rounded-md font-size-14 mx-auto block md:font-size-16 md:py-4">
                  <a
                    href="https://liff.line.me/2002843084-E1bqwPzZ/landing?follow=%40007nfzzj&lp=CxMUwm&liff_id=2002843084-E1bqwPzZ"
                    target="_blank"
                    rel="noopener"
                  >
                    LINEで相談する（お友だち追加）
                  </a>
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="overflow-y-auto">
      {plansGroupedByRoomType.map((group, index) => (
        <div key={`groupByRoomType-${index}`} className="mb-4">
          <div className="font-size-18 font-bold mb-3 ml-3 md:ml-0">{group.roomType}</div>
          {group.planGroups.map((planGroup) => (
            <div key={`planGroup-${planGroup.roomId}`}>
              <PlanGroup planGroup={planGroup} noAccommodation={group.roomType === noAccommodationLabel} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
